import { useQuery, useQueryClient } from "react-query";
import { request } from "../../../config/helpers/axios-instance";
import { throwIfRequestError } from "../../hooks/request-error";

// Prefix for every page/search variant -- the mutations that change shortages
// (useCreatePurchaseOrders, useReceivePurchaseOrderLines, useSetItemStripSize) invalidate
// ["pharmacy-procurement-shortages"], which matches all of them.
const QUERY_KEY = "pharmacy-procurement-shortages";

// GET /api/inventory/shortages answers one page when called with `page`:
//   { rows, total, page, pageSize, pageCount, counts: { all, pending, placed }, readAt, lapsingEnabled }
// An API without pagination answers with the bare array instead; normalised to the same shape
// (counts/lapsingEnabled null = "derive them from the rows", as the screen always did).
export const normalizeShortagesPage = (data) => {
    if (Array.isArray(data)) {
        return {
            rows: data,
            total: data.length,
            page: 1,
            pageSize: data.length || 1,
            pageCount: 1,
            counts: null,
            readAt: data[0]?.readAt || null,
            lapsingEnabled: null,
        };
    }
    return {
        rows: data?.rows || [],
        total: data?.total ?? 0,
        page: data?.page ?? 1,
        pageSize: data?.pageSize ?? 1,
        pageCount: data?.pageCount ?? 1,
        counts: data?.counts || null,
        readAt: data?.readAt || null,
        lapsingEnabled: data?.lapsingEnabled ?? null,
    };
};

const fetchShortages = async (params) => {
    const result = await request({ url: "inventory/shortages", method: "GET", params });
    throwIfRequestError(result);
    const body = result.data;
    if (!body?.success) throw body;
    return normalizeShortagesPage(body.data);
};

// GET /api/inventory/shortages — one page at a time (2026-10-26). The API reads eVital stock
// live for the medicines on the requested page ONLY: reading every medicine with demand on
// every load (~1,600 get_batches calls) used up eVital's hourly limit and blanked the screen.
// `params` = { page, pageSize, search, view, filter }.
//
// Moved from src/hooks/fetch-hooks/get-inventory-shortages/fetch-shortages.js — same
// endpoint, now on the v2 hooks convention (throwIfRequestError) so a request failure
// actually reaches react-query's isError instead of resolving as a false success (see
// src/v2/hooks/request-error.js).
//
// `refreshFresh(medicineIds)` — the stock-sync dialog's "I've updated eVital"
// (.scratch/stock-sync-dialog/SPEC.md). Same page with `freshMedicineIds`, so those
// medicines are read live from eVital instead of through the API's 300s on-hand cache; the
// response replaces this page's data, so every row re-renders with recomputed purchase
// quantities. Resolves to the page's rows. Rejects on failure, like the normal load.
export default function useInventoryShortages({ params = {}, enabled = true } = {}) {
    const queryClient = useQueryClient();
    const queryKey = [QUERY_KEY, params];
    // keepPreviousData: the current page stays on screen while the next one loads.
    const query = useQuery(queryKey, () => fetchShortages(params), { enabled, retry: false, keepPreviousData: true });

    const refreshFresh = async (medicineIds) => {
        const data = await fetchShortages({ ...params, freshMedicineIds: medicineIds.join(",") });
        queryClient.setQueryData(queryKey, data);
        return data.rows;
    };

    return { ...query, refreshFresh };
}
